import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import FeedCard from "@/components/feed/FeedCard";
import StoryStrip from "@/components/feed/StoryStrip";
import CreatorList from "@/components/feed/CreatorList";
import ExclusiveTab from "@/components/feed/ExclusiveTab";
// iter161 · ReelsTab entfernt — Posts unterstützen jetzt Bild+Video, keine Reel-Kategorie mehr.
import { TabBar } from "@/components/toggles";
import GuestWelcomeCard from "@/components/home/GuestWelcomeCard";
import FollowSuggestionsRow from "@/components/home/FollowSuggestionsRow";

const VALID_TABS = new Set(["feed", "creator", "exklusiv"]);

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // iter120 · Message 960: Tab-State via URL-Param, damit die rechte Desktop-Spalte
  // (DesktopSuggestions) den gleichen Tab liest/schreibt wie die Mobile-Tabbar.
  const urlTab = searchParams.get("tab");
  const tab = VALID_TABS.has(urlTab) ? urlTab : "feed";
  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "feed") params.delete("tab"); else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };
  const [posts, setPosts] = useState([]);
  const [storyGroups, setStoryGroups] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const _codes = (user?.capabilities || []).map((c) => c?.capability_code || c);
  const canCreate = !!user && user !== false && (
    ["ADMIN", "owner"].includes(user?.role) || user?.is_admin ||
    _codes.includes("ST_CREATOR") || user?.st_creator === true
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [feedRes, storiesRes, creatorsRes] = await Promise.all([
        api.get("/home/feed"),
        api.get("/stories/entries"),
        api.get("/home/creators"),
      ]);
      setPosts(feedRes.data.posts || []);
      setStoryGroups(storiesRes.data.story_groups || []);
      setCreators(creatorsRes.data.creators || []);
    } catch (err) {
      console.error("Feed load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // iter78: refresh feed when FeedComposer (rendered in AppShell) publishes
  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener("feed-refresh", onRefresh);
    return () => window.removeEventListener("feed-refresh", onRefresh);
  }, [load]);

  return (
    <div data-testid="home-page" className="w-full max-w-2xl 2xl:max-w-full mx-auto 2xl:mx-0">
      {/* Auth Banner for anonymous users → hochwertige GuestWelcomeCard (iter149) */}
      {(user === false || user === null) && !loading && <GuestWelcomeCard />}

      {/* iter313 · Eine Reihe: Erstellen-Kachel (öffnet Composer) + Stories */}
      <StoryStrip
        storyGroups={storyGroups}
        loading={loading}
        canCreate={canCreate}
        onCreate={() => window.dispatchEvent(new CustomEvent("open-feed-composer", { detail: { area: "SOCIAL" } }))}
      />

      {/* „Vorschläge für dich" — nur Social, nur Mobile (Desktop: rechte Spalte) */}
      <FollowSuggestionsRow user={user} />

      {/* iter79: 4-Tab Home-Toggle (Spec: Beiträge / Reels / Creator / Exklusiv)
          iter120: Auf Desktop (2xl) wandern diese Tabs in die rechte Spalte
          (DesktopSuggestions). Hier ausgeblendet, damit sie nicht doppelt erscheinen. */}
      <div className="2xl:hidden">
        <TabBar
          tabs={[
            { key: "feed", label: t("home.tab_feed"), testId: "tab-feed" },
            { key: "creator", label: t("home.tab_creator"), testId: "tab-creator" },
            { key: "exklusiv", label: t("home.tab_exclusive"), testId: "tab-exklusiv" },
          ]}
          activeKey={tab}
          onChange={setTab}
        />
      </div>
      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      ) : tab === "feed" ? (
        <div data-testid="feed-list">
          {posts.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-[#999]">
              {t("home.empty_posts")}
            </div>
          ) : (
            (() => {
              // iter79 Batch 4: Client-side Filter für hidden posts + muted authors
              const hidden = new Set(JSON.parse(localStorage.getItem("hidden_posts") || "[]"));
              const muted = new Set(JSON.parse(localStorage.getItem("muted_authors") || "[]"));
              return posts
                .filter((p) => !hidden.has(p.post_id) && !muted.has(p.author_id))
                .map((p) => <FeedCard key={p.post_id} post={p} />);
            })()
          )}
        </div>
      ) : tab === "creator" ? (
        <CreatorList creators={creators} />
      ) : (
        <ExclusiveTab />
      )}
    </div>
  );
}
